import { jwtDecode } from 'jwt-decode'

export const ONSHAPE_JWT_STORAGE_KEY = 'q20.onshape.jwt.v1'
export const DEFAULT_ONSHAPE_JWT_TTL_MS = 55 * 60 * 1000

export interface StoredOnshapeJwt {
  token: string
  expiresAt: number
}

interface SetOnshapeJwtTokenOptions {
  ttlMs?: number
}

interface DecodedJwtPayload {
  exp?: number
}

const getJwtExpMs = (token: string): number | null => {
  let payload: DecodedJwtPayload

  try {
    payload = jwtDecode<DecodedJwtPayload>(token)
  } catch {
    return null
  }

  if (typeof payload.exp !== 'number' || !Number.isFinite(payload.exp)) {
    return null
  }

  return payload.exp * 1000
}

export const isJwtTokenExpired = (token?: string) => {
  if (!token) return true
  const expMs = getJwtExpMs(token)
  if (!expMs) return false
  return Date.now() >= expMs
}

const getEffectiveExpiresAt = ({
  token,
  issuedAt,
  ttlMs,
}: {
  token: string
  issuedAt: number
  ttlMs: number
}) => {
  const ttlExpiresAt = issuedAt + ttlMs
  const jwtExpMs = getJwtExpMs(token)

  if (!jwtExpMs) {
    return ttlExpiresAt
  }

  return Math.min(ttlExpiresAt, jwtExpMs)
}

export const clearOnshapeJwtToken = () => {
  localStorage.removeItem(ONSHAPE_JWT_STORAGE_KEY)
}

export const setOnshapeJwtToken = (
  token: string,
  options: SetOnshapeJwtTokenOptions = {},
) => {
  const issuedAt = Date.now()
  const ttlMs = options.ttlMs ?? DEFAULT_ONSHAPE_JWT_TTL_MS

  const expiresAt = getEffectiveExpiresAt({
    token,
    issuedAt,
    ttlMs,
  })

  // Guard against stale tokens so readers never receive an already-expired value.
  if (expiresAt <= issuedAt) {
    clearOnshapeJwtToken()
    return
  }

  const payload: StoredOnshapeJwt = {
    token,
    expiresAt,
  }

  localStorage.setItem(ONSHAPE_JWT_STORAGE_KEY, JSON.stringify(payload))
}

export const getStoredOnshapeJwt = (): StoredOnshapeJwt | null => {
  const raw = localStorage.getItem(ONSHAPE_JWT_STORAGE_KEY)
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw) as Partial<StoredOnshapeJwt>
    const isValidPayload =
      typeof parsed?.token === 'string' && typeof parsed?.expiresAt === 'number'

    if (!isValidPayload) {
      clearOnshapeJwtToken()
      return null
    }

    if (parsed && parsed.expiresAt && Date.now() >= parsed.expiresAt) {
      clearOnshapeJwtToken()
      return null
    }

    if (isJwtTokenExpired(parsed?.token)) {
      clearOnshapeJwtToken()
      return null
    }

    return {
      token: parsed.token as string,
      expiresAt: parsed.expiresAt as number,
    }
  } catch {
    clearOnshapeJwtToken()
    return null
  }
}

export const getOnshapeJwtToken = () => getStoredOnshapeJwt()?.token ?? null
